import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Card {
  id: string;
  name: string;
  price: number;
  imageUrl: string;
  description: string;
  rating?: number;
  reviews?: number;
  features?: string[];
}

interface CartContextType {
  items: Card[];
  addToCart: (card: Card) => void;
  removeFromCart: (cardId: string) => void;
  clearCart: () => void;
  total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<Card[]>([]);

  const addToCart = (card: Card) => {
    setItems((prevItems) => [...prevItems, card]);
  };

  const removeFromCart = (cardId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== cardId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const total = items.reduce((sum, item) => sum + item.price, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        clearCart,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 